import {
  S3Client,
  ListObjectsV2Command,
  ListObjectsV2CommandInput,
  ListObjectsV2CommandOutput,
  GetObjectCommand,
  HeadObjectCommand,
  HeadObjectCommandInput,
  PutObjectCommand,
  CreateMultipartUploadCommand,
  UploadPartCommand,
  CompleteMultipartUploadCommand,
  AbortMultipartUploadCommand,
} from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { getCurrentUser } from "../utils/firebase";
import { AWSCredentialManager } from "../utils/awsCredentialManager";
import { getS3Cache, S3MetadataCache } from "../utils/s3Cache";
import { validateS3Key, validateS3Prefix } from "../utils/s3PathValidator";
import JSZip from "jszip";
import { v4 as uuidv4 } from "uuid";

// Types for S3 objects and folders
export interface S3Object {
  key: string;
  name: string;
  size: number;
  lastModified: Date;
  type: "file";
  etag?: string;
  contentType?: string;
}

export interface S3Folder {
  key: string;
  name: string;
  type: "folder";
}

export type S3Item = S3Object | S3Folder;

export interface S3ListResult {
  items: S3Item[];
  nextContinuationToken?: string;
  isTruncated: boolean;
  totalCount: number;
}

export interface S3ServiceConfig {
  bucketName: string;
  region: string;
  signedUrlExpirationMinutes?: number;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export enum AWSErrorType {
  NetworkError = "NetworkError",
  PermissionDenied = "PermissionDenied",
  NotFound = "NotFound",
  Throttling = "Throttling",
  ServiceError = "ServiceError",
  ClientError = "ClientError",
  Validation = "Validation",
  Unknown = "Unknown",
  BadRequest = "BadRequest",
  Conflict = "Conflict",
  Internal = "Internal",
  Timeout = "Timeout",
  InvalidCredentials = "InvalidCredentials",
  BucketNotFound = "BucketNotFound",
  ObjectNotFound = "ObjectNotFound",
  AccessDenied = "AccessDenied",
  RateExceeded = "RateExceeded",
  BadDigest = "BadDigest",
  InvalidRange = "InvalidRange",
  PreconditionFailed = "PreconditionFailed",
  Redirect = "Redirect",
  SlowDown = "SlowDown",
  TemporaryRedirect = "TemporaryRedirect",
  MalformedXML = "MalformedXML",
  NoSuchBucket = "NoSuchBucket",
  NoSuchKey = "NoSuchKey",
  QuotaExceeded = "QuotaExceeded",
  SignatureDoesNotMatch = "SignatureDoesNotMatch",
  ExpiredToken = "ExpiredToken",
  InvalidToken = "InvalidToken",
  MissingRequiredParameter = "MissingRequiredParameter",
  RequestTimeout = "RequestTimeout",
  ServiceUnavailable = "ServiceUnavailable",
  TooManyRequests = "TooManyRequests",
  InternalFailure = "InternalFailure",
  InvalidRequest = "InvalidRequest",
  InvalidParameter = "InvalidParameter",
  ResourceNotFound = "ResourceNotFound",
  Forbidden = "Forbidden",
  Unauthorized = "Unauthorized",
  NotImplemented = "NotImplemented",
  BadGateway = "BadGateway",
  ServiceUnavailableTransient = "ServiceUnavailableTransient",
  GatewayTimeout = "GatewayTimeout",
  InsufficientStorage = "InsufficientStorage",
  LoopDetected = "LoopDetected",
  NotExtended = "NotExtended",
  NetworkAuthenticationRequired = "NetworkAuthenticationRequired",
  ConnectionRefused = "ConnectionRefused",
  HostUnreachable = "HostUnreachable",
  NameResolutionFailed = "NameResolutionFailed",
  SSLHandshakeFailed = "SSLHandshakeFailed",
  ConnectionReset = "ConnectionReset",
  BrokenPipe = "BrokenPipe",
  Aborted = "Aborted",
  Canceled = "Canceled",
  DeadlineExceeded = "DeadlineExceeded",
  Unavailable = "Unavailable",
  DataLoss = "DataLoss",
  Unauthenticated = "Unauthenticated",
  PermissionError = "PermissionError",
  QuotaError = "QuotaError",
  ThrottlingError = "ThrottlingError",
  RetryableError = "RetryableError",
  NonRetryableError = "NonRetryableError",
  ClientSideError = "ClientSideError",
  ServerSideError = "ServerSideError",
  UnknownError = "UnknownError",
}

function classifyAWSError(
  error: any,
  defaultMessage: string,
  statusCode?: number
): {
  errorCategory: AWSErrorType;
  userMessage: string;
  technicalMessage: string;
  isRetryable: boolean;
  retryAfter?: number;
} {
  let errorCategory: AWSErrorType = AWSErrorType.Unknown;
  let userMessage: string = defaultMessage;
  let technicalMessage: string = error?.message || defaultMessage;
  let isRetryable: boolean = false;
  let retryAfter: number | undefined = undefined;

  if (error && typeof error === "object") {
    const awsErrorCode = error.code || error.name;
    const httpStatusCode = statusCode || error.$metadata?.httpStatusCode;

    switch (awsErrorCode) {
      case "AccessDenied":
      case "UnauthorizedOperation":
      case "SignatureDoesNotMatch":
      case "InvalidAccessKeyId":
      case "AuthFailure":
      case "Forbidden":
        errorCategory = AWSErrorType.PermissionDenied;
        userMessage =
          "Access denied. You do not have permission to perform this action. Please contact your administrator for assistance.";
        isRetryable = false;
        break;
      case "NoSuchBucket":
      case "NotFound":
      case "NoSuchKey":
        errorCategory = AWSErrorType.NotFound;
        userMessage =
          "The requested resource was not found. It might have been moved, deleted, or you do not have access. Please verify the path or contact support.";
        isRetryable = false;
        break;
      case "Throttling":
      case "ProvisionedThroughputExceededException":
      case "TooManyRequestsException":
      case "SlowDown":
      case "BandwidthLimitExceeded":
      case "RequestLimitExceeded":
        errorCategory = AWSErrorType.Throttling;
        userMessage =
          "Service is currently experiencing high demand. Please wait a few moments and try your request again.";
        isRetryable = true;
        retryAfter = 5000; // 5 seconds
        break;
      case "NetworkingError":
      case "TimeoutError":
      case "RequestTimeout":
      case "ConnectionError":
      case "ECONNREFUSED":
      case "EHOSTUNREACH":
      case "ENOTFOUND":
        errorCategory = AWSErrorType.NetworkError;
        userMessage =
          "A network connection issue occurred. Please check your internet connection and try again. If the problem persists, contact support.";
        isRetryable = true;
        retryAfter = 3000; // 3 seconds
        break;
      case "ValidationException":
      case "InvalidParameter":
      case "MissingRequiredParameter":
      case "MalformedXML":
      case "InvalidRequest":
      case "BadRequest":
        errorCategory = AWSErrorType.Validation;
        userMessage =
          "The information provided is invalid or incomplete. Please review your input and try again.";
        isRetryable = false;
        break;
      case "InternalError":
      case "ServiceUnavailable":
      case "InternalFailure":
      case "ServiceUnavailableException":
      case "InternalServiceError":
        errorCategory = AWSErrorType.ServiceError;
        userMessage =
          "An unexpected internal error occurred. Our team has been notified and is working to resolve the issue. Please try again later.";
        isRetryable = true;
        retryAfter = 10000; // 10 seconds
        break;
      case "ExpiredToken":
      case "InvalidToken":
        errorCategory = AWSErrorType.InvalidToken;
        userMessage =
          "Your session has expired or is invalid. Please log in again to continue.";
        isRetryable = false;
        break;
      default:
        // Fallback based on HTTP status codes
        if (httpStatusCode) {
          if (httpStatusCode >= 400 && httpStatusCode < 500) {
            errorCategory = AWSErrorType.ClientError;
            userMessage =
              "A client-side error occurred with your request. Please review the details and try again.";
            isRetryable = false;
          } else if (httpStatusCode >= 500 && httpStatusCode < 600) {
            errorCategory = AWSErrorType.ServerSideError;
            userMessage =
              "A server-side error occurred. We are working to fix this. Please try again later.";
            isRetryable = true;
            retryAfter = 5000;
          }
        }
        break;
    }

    // Refine technical message
    if (error.message) {
      technicalMessage = error.message;
    } else if (error.Code && error.Message) {
      technicalMessage = `${error.Code}: ${error.Message}`;
    } else if (awsErrorCode) {
      technicalMessage = `AWS Error Code: ${awsErrorCode}`;
    }
  }

  return {
    errorCategory,
    userMessage,
    technicalMessage,
    isRetryable,
    retryAfter,
  };
}

export class S3ServiceError extends Error {
  public correlationId: string;
  public errorCategory: AWSErrorType;
  public userMessage: string;
  public technicalMessage: string;
  public isRetryable: boolean;
  public retryAfter?: number; // in milliseconds

  constructor(
    message: string,
    public code?: string,
    public statusCode?: number,
    public originalError?: Error,
    correlationId?: string
  ) {
    super(message);
    this.name = "S3ServiceError";
    this.correlationId = correlationId || uuidv4();

    const {
      errorCategory,
      userMessage,
      technicalMessage,
      isRetryable,
      retryAfter,
    } = classifyAWSError(originalError, message, statusCode);

    this.errorCategory = errorCategory;
    this.userMessage = userMessage;
    this.technicalMessage = technicalMessage;
    this.isRetryable = isRetryable;
    this.retryAfter = retryAfter;
  }
}

interface LogEntry {
  timestamp: string;
  level: "info" | "warn" | "error";
  message: string;
  correlationId?: string;
  userId?: string;
  operation?: string;
  params?: Record<string, any>;
  errorCategory?: AWSErrorType;
  technicalMessage?: string;
  isRetryable?: boolean;
  statusCode?: number;
  awsCode?: string;
}

class ErrorLogger {
  private static instance: ErrorLogger;

  private constructor() {}

  public static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  private getUserId(): string | undefined {
    const currentUser = getCurrentUser();
    return currentUser?.uid;
  }

  private sanitizeParams(params: Record<string, any>): Record<string, any> {
    const sanitized: Record<string, any> = { ...params };
    // Example: remove sensitive keys
    if (sanitized.accessKeyId) sanitized.accessKeyId = "[REDACTED]";
    if (sanitized.secretAccessKey) sanitized.secretAccessKey = "[REDACTED]";
    if (sanitized.sessionToken) sanitized.sessionToken = "[REDACTED]";
    if (sanitized.credentials) {
      if (sanitized.credentials.accessKeyId)
        sanitized.credentials.accessKeyId = "[REDACTED]";
      if (sanitized.credentials.secretAccessKey)
        sanitized.credentials.secretAccessKey = "[REDACTED]";
      if (sanitized.credentials.sessionToken)
        sanitized.credentials.sessionToken = "[REDACTED]";
    }
    return sanitized;
  }

  public error(
    error: S3ServiceError,
    operation: string,
    params: Record<string, any> = {}
  ): void {
    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: "error",
      message: error.message,
      correlationId: error.correlationId,
      userId: this.getUserId(),
      operation: operation,
      params: this.sanitizeParams(params),
      errorCategory: error.errorCategory,
      technicalMessage: error.technicalMessage,
      isRetryable: error.isRetryable,
      statusCode: error.statusCode,
      awsCode: error.code,
    };
    console.error("S3_ERROR_LOG:", JSON.stringify(logEntry, null, 2));
  }

  public info(message: string, context?: Record<string, any>): void {
    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: "info",
      message: message,
      userId: this.getUserId(),
      ...context,
    };
    //console.log("S3_INFO_LOG:", JSON.stringify(logEntry, null, 2));
  }

  public warn(message: string, context?: Record<string, any>): void {
    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: "warn",
      message: message,
      userId: this.getUserId(),
      ...context,
    };
    console.warn("S3_WARN_LOG:", JSON.stringify(logEntry, null, 2));
  }
}

const errorLogger = ErrorLogger.getInstance();

/**
 * S3 Service class for handling all S3 operations
 */
export class S3Service {
  private s3Client: S3Client | null = null;
  private config: S3ServiceConfig;
  private cache: S3MetadataCache;

  constructor(config?: Partial<S3ServiceConfig>) {
    this.config = {
      bucketName: process.env.REACT_APP_S3_BUCKET || "",
      region: process.env.REACT_APP_S3_REGION || "us-east-1",
      signedUrlExpirationMinutes: 15, // Default 15 minutes
      ...config,
    };

    if (!this.config.bucketName) {
      throw new S3ServiceError(
        "S3 bucket name is required. Set REACT_APP_S3_BUCKET environment variable."
      );
    }

    // Initialize cache with debug mode in development
    this.cache = getS3Cache({
      ttlMs: 5 * 60 * 1000, // 5 minutes
      maxEntries: 100,
      debug: process.env.NODE_ENV === "development",
    });
  }

  /**
   * Initialize S3 client with credentials based on authentication state
   */
  private async initializeClient(): Promise<S3Client> {
    try {
      // Check if user is authenticated via Firebase or AWS Cognito
      let currentUser = getCurrentUser();
      let isAuthenticated = currentUser !== null;

      // If no Firebase user, check for persisted AWS Cognito auth
      if (!isAuthenticated) {
        try {
          const persistedUser = localStorage.getItem("aws-cognito-user");
          if (persistedUser) {
            const userData = JSON.parse(persistedUser);
            if (userData.uid) {
              isAuthenticated = true;
              console.log(
                "Current user: AWS Cognito authenticated user",
                userData.email
              );
            }
          }
        } catch (error) {
          console.warn("Failed to check persisted AWS Cognito auth:", error);
        }
      } else {
        console.log(
          "Current user: Firebase authenticated user",
          currentUser?.email
        );
      }

      console.log(
        "Authentication status:",
        isAuthenticated ? "authenticated" : "unauthenticated"
      );

      // Get appropriate credentials based on authentication state
      const credentials =
        AWSCredentialManager.getCredentialsForUser(isAuthenticated);

      // Validate credentials before using them
      const validation = AWSCredentialManager.validateCredentials(credentials);
      if (!validation.isValid) {
        throw new S3ServiceError(
          `Invalid AWS credentials: ${validation.errors.join(", ")}`,
          "INVALID_CREDENTIALS"
        );
      }

      this.s3Client = new S3Client({
        region: credentials.region,
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          // Note: sessionToken not needed for IAM user credentials
        },
      });

      // Log credential selection for debugging (without exposing sensitive data)
      errorLogger.info(
        `S3Client initialized with ${
          isAuthenticated ? "authenticated" : "unauthenticated"
        } credentials for user: ${
          isAuthenticated ? currentUser?.email || "unknown" : "anonymous"
        }`
      );

      return this.s3Client;
    } catch (error) {
      const s3Error = new S3ServiceError(
        "Failed to initialize S3 client. Please check your configuration and network connection.",
        "INIT_CLIENT_FAILED",
        undefined,
        error instanceof Error ? error : undefined
      );
      errorLogger.error(s3Error, "initializeClient", { originalError: error });
      throw s3Error;
    }
  }

  /**
   * Get current S3 client or initialize if needed
   */
  private async getClient(): Promise<S3Client> {
    if (!this.s3Client) {
      return await this.initializeClient();
    }
    return this.s3Client;
  }

  /**
   * Retry helper with exponential backoff for transient errors
   * @param command - The AWS SDK command to execute
   * @param operation - Name of the operation for logging
   * @param maxAttempts - Maximum number of retry attempts (default: 3)
   * @returns The command response
   */
  private async sendWithRetry<T = any>(
    command: any,
    operation: string,
    maxAttempts: number = 3
  ): Promise<T> {
    const baseDelay = 1000; // 1 second base delay
    let lastError: Error | undefined;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const client = await this.getClient();
        return (await client.send(command)) as T;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // Check if error is retryable
        const isRetryable = this.isRetryableError(error);

        if (!isRetryable || attempt === maxAttempts) {
          // Don't retry if error is not retryable or max attempts reached
          throw error;
        }

        // Calculate exponential backoff delay
        const delayMs = baseDelay * Math.pow(2, attempt - 1);

        errorLogger.warn(`Retrying ${operation} after error (attempt ${attempt}/${maxAttempts})`, {
          operation: "sendWithRetry",
          operationName: operation,
          attempt,
          maxAttempts,
          delayMs,
          error: lastError.message,
        });

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    // This shouldn't be reached, but TypeScript needs it
    throw lastError || new Error("Max retry attempts reached");
  }

  /**
   * Check if an error is retryable
   * @param error - The error to check
   * @returns True if the error is retryable
   */
  private isRetryableError(error: any): boolean {
    if (!error) return false;

    const errorCode = error.code || error.name;

    // Retryable error codes
    const retryableErrors = [
      "Throttling",
      "ThrottlingException",
      "ProvisionedThroughputExceededException",
      "TooManyRequestsException",
      "SlowDown",
      "RequestLimitExceeded",
      "BandwidthLimitExceeded",
      "NetworkingError",
      "TimeoutError",
      "RequestTimeout",
      "ConnectionError",
      "ECONNREFUSED",
      "EHOSTUNREACH",
      "ENOTFOUND",
      "InternalError",
      "ServiceUnavailable",
      "InternalFailure",
      "ServiceUnavailableException",
      "InternalServiceError",
    ];

    // Check error code
    if (retryableErrors.includes(errorCode)) {
      return true;
    }

    // Check HTTP status code
    const statusCode = error.$metadata?.httpStatusCode || error.statusCode;
    if (statusCode && (statusCode === 429 || statusCode >= 500)) {
      return true;
    }

    return false;
  }

  /**
   * Normalize S3 response into consistent S3Object metadata
   * @param key - The S3 object key
   * @param file - The uploaded file/blob
   * @param etag - The ETag from the upload response
   * @param contentType - The content type of the file
   * @returns Normalized S3Object metadata
   */
  private normalizeS3Metadata(
    key: string,
    file: File | Blob,
    etag?: string,
    contentType?: string
  ): S3Object {
    // Extract filename from key
    const fileName = key.split("/").pop() || key;

    return {
      key,
      name: fileName,
      size: file.size,
      lastModified: new Date(),
      type: "file",
      etag,
      contentType,
    };
  }

  /**
   * List objects in S3 bucket with hierarchical navigation support
   * @param prefix - The folder prefix to list (e.g., 'reports/2024/')
   * @param delimiter - Delimiter for folder separation (default: '/')
   * @param maxKeys - Maximum number of objects to return (default: 1000)
   * @param continuationToken - Token for pagination
   * @param useCache - Whether to use caching (default: true)
   */
  async listObjects(
    prefix: string = "",
    delimiter: string = "/",
    maxKeys: number = 1000,
    continuationToken?: string,
    useCache: boolean = true
  ): Promise<S3ListResult> {
    // Validate the prefix before proceeding
    validateS3Prefix(prefix);

    // Check cache first if enabled and no continuation token (first page only)
    if (useCache && !continuationToken) {
      const cachedResult = this.cache.get(prefix, delimiter, maxKeys);
      if (cachedResult) {
        return cachedResult;
      }
    }

    try {
      const client = await this.getClient();
      const queryStartTime = Date.now();

      const params: ListObjectsV2CommandInput = {
        Bucket: this.config.bucketName,
        Prefix: prefix,
        Delimiter: delimiter,
        MaxKeys: maxKeys,
        ContinuationToken: continuationToken,
      };

      const command = new ListObjectsV2Command(params);
      const response: ListObjectsV2CommandOutput = await client.send(command);

      // Track S3 API query latency
      const queryLatency = Date.now() - queryStartTime;
      this.cache.recordQueryLatency(queryLatency);

      const items: S3Item[] = [];

      // Add folders (CommonPrefixes)
      if (response.CommonPrefixes && response.CommonPrefixes.length > 0) {
        for (const folder of response.CommonPrefixes) {
          if (folder.Prefix) {
            const folderName = folder.Prefix.replace(prefix, "").replace(
              delimiter,
              ""
            );
            if (folderName) {
              items.push({
                key: folder.Prefix,
                name: folderName,
                type: "folder",
              });
            }
          }
        }
      }

      // Add files (Contents)
      if (response.Contents && response.Contents.length > 0) {
        for (const object of response.Contents) {
          if (object.Key && object.Key !== prefix) {
            // Exclude the prefix itself
            const fileName = object.Key.replace(prefix, "");
            if (fileName && !fileName.endsWith(delimiter)) {
              // Exclude folder markers
              items.push({
                key: object.Key,
                name: fileName,
                size: object.Size || 0,
                lastModified: object.LastModified || new Date(),
                type: "file",
                etag: object.ETag,
              });
            }
          }
        }
      }

      const result: S3ListResult = {
        items,
        nextContinuationToken: response.NextContinuationToken,
        isTruncated: response.IsTruncated || false,
        totalCount: items.length,
      };

      // Track API call for metrics
      this.cache.trackApiCall();

      // Cache the result if caching is enabled and this is the first page
      if (useCache && !continuationToken) {
        // Use partition-based caching for better aggregation queries
        this.cache.setWithPartition(result, prefix, delimiter, maxKeys);
      }

      if (process.env.NODE_ENV === "development") {
        errorLogger.info(`S3 API call completed in ${queryLatency}ms for prefix: "${prefix}"`, {
          operation: "listObjects",
          queryLatency,
          itemCount: items.length,
        });
      }

      return result;
    } catch (error) {
      console.error("ListObjects error:", error);
      const s3Error = new S3ServiceError(
        "Failed to list objects. Please try again later.",
        "LIST_OBJECTS_FAILED",
        undefined,
        error instanceof Error ? error : undefined
      );
      errorLogger.error(s3Error, "listObjects", {
        prefix,
        delimiter,
        maxKeys,
        continuationToken,
      });
      throw s3Error;
    }
  }

  /**
   * Get object metadata without downloading the content
   * @param key - The S3 object key
   */
  async getObjectMetadata(key: string): Promise<S3Object> {
    // Validate the key before proceeding
    validateS3Key(key);

    try {
      const client = await this.getClient();

      const params: HeadObjectCommandInput = {
        Bucket: this.config.bucketName,
        Key: key,
      };

      const command = new HeadObjectCommand(params);
      const response = await client.send(command);

      const fileName = key.split("/").pop() || key;

      return {
        key,
        name: fileName,
        size: response.ContentLength || 0,
        lastModified: response.LastModified || new Date(),
        type: "file",
        etag: response.ETag,
        contentType: response.ContentType,
      };
    } catch (error) {
      const s3Error = new S3ServiceError(
        "Failed to get object metadata. The requested object might not exist or you do not have permission.",
        "GET_METADATA_FAILED",
        undefined,
        error instanceof Error ? error : undefined
      );
      errorLogger.error(s3Error, "getObjectMetadata", { key });
      throw s3Error;
    }
  }

  /**
   * Download object content as a Blob
   * @param key - The S3 object key
   */
  async downloadObject(
    key: string,
    onProgress?: (progress: number) => void
  ): Promise<Blob> {
    // Validate the key before proceeding
    validateS3Key(key);

    try {
      // Generate a signed URL for the object
      const signedUrl = await this.generateSignedUrl(key);

      // Fetch the object content using the signed URL
      const response = await fetch(signedUrl);

      if (!response.ok) {
        throw new S3ServiceError(
          `Failed to fetch object from signed URL: ${response.statusText}`,
          "FETCH_FAILED",
          response.status
        );
      }

      if (!response.body) {
        throw new S3ServiceError("Response body is empty");
      }

      const reader = response.body.getReader();
      const contentLength = response.headers.get("Content-Length");
      const totalLength = contentLength ? parseInt(contentLength, 10) : 0;
      let receivedLength = 0;
      const chunks: Uint8Array[] = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        chunks.push(value);
        receivedLength += value.length;

        if (onProgress && totalLength > 0) {
          const progress = (receivedLength / totalLength) * 100;
          onProgress(Math.min(progress, 100)); // Ensure progress doesn't exceed 100
        }
      }

      // Combine chunks into a single Uint8Array
      const combinedArray = new Uint8Array(receivedLength);
      let offset = 0;
      for (const chunk of chunks) {
        combinedArray.set(chunk, offset);
        offset += chunk.length;
      }

      return new Blob([combinedArray], {
        type:
          response.headers.get("Content-Type") || "application/octet-stream",
      });
    } catch (error) {
      const s3Error = new S3ServiceError(
        "Failed to download object. Please check your network connection or try again later.",
        "DOWNLOAD_FAILED",
        undefined,
        error instanceof Error ? error : undefined
      );
      errorLogger.error(s3Error, "downloadObject", { key });
      throw s3Error;
    }
  }

  /**
   * Download multiple S3 objects as a single ZIP file.
   * @param objects - Array of S3Object to download and compress.
   * @param onProgress - Optional callback for progress updates (0-100).
   * @returns A Promise that resolves to a Blob representing the ZIP file.
   */
  async downloadObjectsAsZip(
    objects: S3Object[],
    onProgress?: (progress: number) => void
  ): Promise<Blob> {
    const zip = new JSZip();
    let filesProcessed = 0;

    try {
      for (const obj of objects) {
        const fileBlob = await this.downloadObject(obj.key);
        zip.file(obj.name, fileBlob);
        filesProcessed++;
        if (onProgress) {
          onProgress(Math.floor((filesProcessed / objects.length) * 100));
        }
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      return zipBlob;
    } catch (error) {
      const s3Error = new S3ServiceError(
        "Failed to create ZIP archive. Please try again.",
        "ZIP_CREATION_FAILED",
        undefined,
        error instanceof Error ? error : undefined
      );
      errorLogger.error(s3Error, "downloadObjectsAsZip", {
        objects: objects.map((o) => o.key),
      });
      throw s3Error;
    }
  }

  /**
   * Generate a signed URL for secure file access
   * @param key - The S3 object key
   * @param expirationMinutes - URL expiration time in minutes (default: 15)
   */
  async generateSignedUrl(
    key: string,
    expirationMinutes?: number
  ): Promise<string> {
    // Validate the key before proceeding
    validateS3Key(key);

    try {
      const client = await this.getClient();

      const expiresIn =
        (expirationMinutes || this.config.signedUrlExpirationMinutes || 15) *
        60; // Convert to seconds

      const command = new GetObjectCommand({
        Bucket: this.config.bucketName,
        Key: key,
      });

      const signedUrl = await getSignedUrl(client, command, {
        expiresIn,
      });

      return signedUrl;
    } catch (error) {
      const s3Error = new S3ServiceError(
        "Failed to generate signed URL. Please ensure the object exists and you have permissions.",
        "SIGNED_URL_FAILED",
        undefined,
        error instanceof Error ? error : undefined
      );
      errorLogger.error(s3Error, "generateSignedUrl", {
        key,
        expirationMinutes,
      });
      throw s3Error;
    }
  }

  /**
   * Check if a specific object exists in the bucket
   * @param key - The S3 object key
   */
  async objectExists(key: string): Promise<boolean> {
    // Validate the key before proceeding
    validateS3Key(key);

    try {
      await this.getObjectMetadata(key);
      return true;
    } catch (error) {
      if (error instanceof S3ServiceError && error.originalError) {
        const awsError = error.originalError as any;
        if (
          awsError.name === "NotFound" ||
          awsError.$metadata?.httpStatusCode === 404
        ) {
          errorLogger.info(`Object not found: ${key}`, {
            operation: "objectExists",
            key,
            errorCategory: AWSErrorType.NotFound,
          });
          return false;
        }
      }
      const s3Error = new S3ServiceError(
        "An unexpected error occurred while checking object existence.",
        "OBJECT_EXISTS_FAILED",
        undefined,
        error instanceof Error ? error : undefined
      );
      errorLogger.error(s3Error, "objectExists", { key });
      throw s3Error;
    }
  }

  /**
   * Upload a single file to S3 (automatically chooses single or multipart based on size)
   * @param file - The file or blob to upload
   * @param key - The S3 object key (destination path)
   * @param onProgress - Optional callback for upload progress updates
   * @returns S3Object metadata for the uploaded file
   */
  async uploadFile(
    file: File | Blob,
    key: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<S3Object> {
    console.log('[S3Service] uploadFile called', {
      key,
      fileSize: file.size,
      fileName: file instanceof File ? file.name : 'blob',
      hasProgressCallback: !!onProgress,
    });

    // Validate the key before proceeding
    validateS3Key(key);

    const maxSize = 100 * 1024 * 1024; // 100MB

    // Automatically use multipart upload for files larger than 100MB
    if (file.size > maxSize) {
      console.log('[S3Service] Using multipart upload for large file');
      return await this.uploadFileMultipart(file, key, onProgress);
    }

    try {
      console.log('[S3Service] Getting S3 client');
      const client = await this.getClient();
      console.log('[S3Service] S3 client obtained');

      // Determine content type
      const contentType =
        file instanceof File ? file.type : "application/octet-stream";

      // Report initial progress if callback provided
      if (onProgress) {
        console.log('[S3Service] Calling initial progress callback');
        onProgress({
          loaded: 0,
          total: file.size,
          percentage: 0,
        });
      }

      // Convert File/Blob to Uint8Array for AWS SDK v3 browser compatibility
      console.log('[S3Service] Converting file to Uint8Array');
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      console.log('[S3Service] File converted to Uint8Array', { size: uint8Array.byteLength });

      const command = new PutObjectCommand({
        Bucket: this.config.bucketName,
        Key: key,
        Body: uint8Array,
        ContentType: contentType,
      });

      console.log('[S3Service] Sending PutObjectCommand', {
        bucket: this.config.bucketName,
        key,
        contentType,
      });

      const uploadStartTime = Date.now();

      // Send command with retry logic for transient errors
      const response = await this.sendWithRetry(command, "uploadFile");

      const uploadDuration = Date.now() - uploadStartTime;

      console.log('[S3Service] Upload completed', {
        key,
        uploadDurationMs: uploadDuration,
        etag: response.ETag,
      });

      // Report completion if callback provided
      if (onProgress) {
        console.log('[S3Service] Calling completion progress callback');
        onProgress({
          loaded: file.size,
          total: file.size,
          percentage: 100,
        });
      }

      errorLogger.info(`File uploaded successfully: ${key}`, {
        operation: "uploadFile",
        key,
        size: file.size,
        contentType,
        uploadDurationMs: uploadDuration,
        etag: response.ETag,
      });

      // Return normalized S3Object metadata
      return this.normalizeS3Metadata(key, file, response.ETag, contentType);
    } catch (error) {
      console.error('[S3Service] Upload failed', { key, error });
      const s3Error = new S3ServiceError(
        "Failed to upload file. Please check your network connection and try again.",
        "UPLOAD_FAILED",
        undefined,
        error instanceof Error ? error : undefined
      );
      errorLogger.error(s3Error, "uploadFile", { key, size: file.size });
      throw s3Error;
    }
  }

  /**
   * Upload a large file using multipart upload (for files >100MB)
   * @param file - The file or blob to upload
   * @param key - The S3 object key (destination path)
   * @param onProgress - Optional callback for upload progress updates
   * @returns S3Object metadata for the uploaded file
   */
  async uploadFileMultipart(
    file: File | Blob,
    key: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<S3Object> {
    // Validate the key before proceeding
    validateS3Key(key);

    const partSize = 5 * 1024 * 1024; // 5MB (minimum part size per AWS requirements)
    let uploadId: string | undefined;

    try {
      const client = await this.getClient();

      // Determine content type
      const contentType =
        file instanceof File ? file.type : "application/octet-stream";

      // Step 1: Initiate multipart upload
      const createCommand = new CreateMultipartUploadCommand({
        Bucket: this.config.bucketName,
        Key: key,
        ContentType: contentType,
      });

      const createResponse = await this.sendWithRetry(createCommand, "createMultipartUpload");
      uploadId = createResponse.UploadId;

      if (!uploadId) {
        throw new S3ServiceError(
          "Failed to initiate multipart upload: No upload ID received",
          "MULTIPART_INIT_FAILED"
        );
      }

      errorLogger.info(`Multipart upload initiated for: ${key}`, {
        operation: "uploadFileMultipart",
        key,
        uploadId,
        size: file.size,
        totalParts: Math.ceil(file.size / partSize),
      });

      // Step 2: Upload parts
      const parts: { ETag: string; PartNumber: number }[] = [];
      const totalParts = Math.ceil(file.size / partSize);
      let uploadedBytes = 0;

      for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const partBlob = file.slice(start, end);
        
        // Convert Blob to Uint8Array for AWS SDK v3 browser compatibility
        const partArrayBuffer = await partBlob.arrayBuffer();
        const partUint8Array = new Uint8Array(partArrayBuffer);

        const uploadPartCommand = new UploadPartCommand({
          Bucket: this.config.bucketName,
          Key: key,
          PartNumber: partNumber,
          UploadId: uploadId,
          Body: partUint8Array,
        });

        const partResponse = await this.sendWithRetry(uploadPartCommand, `uploadPart-${partNumber}`);

        if (!partResponse.ETag) {
          throw new S3ServiceError(
            `Failed to upload part ${partNumber}: No ETag received`,
            "MULTIPART_PART_FAILED"
          );
        }

        parts.push({
          ETag: partResponse.ETag,
          PartNumber: partNumber,
        });

        // Update progress
        uploadedBytes += partBlob.size;
        if (onProgress) {
          onProgress({
            loaded: uploadedBytes,
            total: file.size,
            percentage: (uploadedBytes / file.size) * 100,
          });
        }

        errorLogger.info(`Uploaded part ${partNumber}/${totalParts}`, {
          operation: "uploadFileMultipart",
          key,
          uploadId,
          partNumber,
          partSize: partBlob.size,
          uploadedBytes,
          totalBytes: file.size,
        });
      }

      // Step 3: Complete multipart upload
      const completeCommand = new CompleteMultipartUploadCommand({
        Bucket: this.config.bucketName,
        Key: key,
        UploadId: uploadId,
        MultipartUpload: {
          Parts: parts,
        },
      });

      const completeResponse = await this.sendWithRetry(completeCommand, "completeMultipartUpload");

      errorLogger.info(`Multipart upload completed successfully: ${key}`, {
        operation: "uploadFileMultipart",
        key,
        uploadId,
        size: file.size,
        totalParts,
        etag: completeResponse.ETag,
      });

      // Return normalized S3Object metadata
      return this.normalizeS3Metadata(key, file, completeResponse.ETag, contentType);
    } catch (error) {
      // Abort the multipart upload if it was initiated
      if (uploadId) {
        try {
          await this.abortUpload(uploadId, key);
        } catch (abortError) {
          // Log but don't throw - the original error is more important
          errorLogger.warn(`Failed to abort multipart upload: ${key}`, {
            operation: "uploadFileMultipart",
            key,
            uploadId,
            error: abortError instanceof Error ? abortError.message : String(abortError),
          });
        }
      }

      const s3Error = new S3ServiceError(
        "Failed to upload file using multipart upload. Please check your network connection and try again.",
        "MULTIPART_UPLOAD_FAILED",
        undefined,
        error instanceof Error ? error : undefined
      );
      errorLogger.error(s3Error, "uploadFileMultipart", {
        key,
        size: file.size,
        uploadId,
      });
      throw s3Error;
    }
  }

  /**
   * Upload multiple files to S3 (batch upload)
   * @param files - Array of files with their destination keys
   * @param onProgress - Optional callback for overall progress updates
   * @returns Array of S3Object metadata for uploaded files
   */
  async uploadFiles(
    files: Array<{ file: File | Blob; key: string }>,
    onProgress?: (progress: UploadProgress & { currentFile: number; totalFiles: number }) => void
  ): Promise<S3Object[]> {
    const results: S3Object[] = [];
    const totalFiles = files.length;
    let totalBytes = 0;
    let uploadedBytes = 0;

    // Calculate total bytes
    for (const { file } of files) {
      totalBytes += file.size;
    }

    // Upload each file
    for (let i = 0; i < totalFiles; i++) {
      const { file, key } = files[i];

      try {
        const result = await this.uploadFile(file, key, (fileProgress) => {
          // Calculate overall progress
          const currentFileBytes = (fileProgress.percentage / 100) * file.size;
          const overallLoaded = uploadedBytes + currentFileBytes;

          if (onProgress) {
            onProgress({
              loaded: overallLoaded,
              total: totalBytes,
              percentage: (overallLoaded / totalBytes) * 100,
              currentFile: i + 1,
              totalFiles,
            });
          }
        });

        results.push(result);
        uploadedBytes += file.size;

        // Report progress after each file completes
        if (onProgress) {
          onProgress({
            loaded: uploadedBytes,
            total: totalBytes,
            percentage: (uploadedBytes / totalBytes) * 100,
            currentFile: i + 1,
            totalFiles,
          });
        }
      } catch (error) {
        // Log error but continue with remaining files
        errorLogger.error(
          new S3ServiceError(
            `Failed to upload file ${i + 1}/${totalFiles}: ${key}`,
            "BATCH_UPLOAD_PARTIAL_FAILURE",
            undefined,
            error instanceof Error ? error : undefined
          ),
          "uploadFiles",
          { key, fileIndex: i, totalFiles }
        );

        // Re-throw if this is a critical error that should stop the batch
        if (error instanceof S3ServiceError && !error.isRetryable) {
          throw error;
        }
      }
    }

    return results;
  }

  /**
   * Abort a multipart upload
   * @param uploadId - The upload ID to abort
   * @param key - The S3 object key
   */
  async abortUpload(uploadId: string, key: string): Promise<void> {
    // Validate the key before proceeding
    validateS3Key(key);

    try {
      const command = new AbortMultipartUploadCommand({
        Bucket: this.config.bucketName,
        Key: key,
        UploadId: uploadId,
      });

      await this.sendWithRetry(command, "abortUpload");

      errorLogger.info(`Successfully aborted multipart upload: ${key}`, {
        operation: "abortUpload",
        key,
        uploadId,
      });
    } catch (error) {
      // Check if the upload was already completed or doesn't exist (idempotent)
      const errorCode = (error as any)?.code || (error as any)?.name;
      if (errorCode === "NoSuchUpload") {
        errorLogger.info(`Multipart upload already completed or doesn't exist: ${key}`, {
          operation: "abortUpload",
          key,
          uploadId,
        });
        return; // Silently succeed for idempotency
      }

      const s3Error = new S3ServiceError(
        "Failed to abort multipart upload.",
        "ABORT_UPLOAD_FAILED",
        undefined,
        error instanceof Error ? error : undefined
      );
      errorLogger.error(s3Error, "abortUpload", { key, uploadId });
      throw s3Error;
    }
  }

  /**
   * Get the bucket name
   */
  getBucketName(): string {
    return this.config.bucketName;
  }

  /**
   * Get the region
   */
  getRegion(): string {
    return this.config.region;
  }

  /**
   * Refresh credentials and reinitialize client
   */
  async refreshCredentials(): Promise<void> {
    this.s3Client = null;
    await this.initializeClient();
  }

  /**
   * Clear the metadata cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Invalidate cache entries for a specific prefix
   */
  invalidateCachePrefix(prefix: string): void {
    // Validate the prefix before proceeding
    validateS3Prefix(prefix);

    this.cache.invalidatePrefix(prefix);
    this.cache.invalidatePartition(prefix);
    this.cache.invalidateFilterMetadata();
  }

  /**
   * Extract and cache filter metadata (partners, years, months) from S3 listings
   * Avoids repeated extraction by caching computed results
   * 
   * @param prefix - Prefix to scan for filter metadata extraction
   * @param maxItems - Maximum items to scan (default: 1000)
   */
  async extractAndCacheFilterMetadata(
    prefix: string = "",
    maxItems: number = 1000
  ): Promise<{ partners: string[]; years: string[]; months: string[] }> {
    try {
      // Check if we have cached filter metadata
      const cachedMetadata = this.cache.getFilterMetadata();
      if (cachedMetadata) {
        return {
          partners: Array.from(cachedMetadata.partners),
          years: Array.from(cachedMetadata.years),
          months: Array.from(cachedMetadata.months),
        };
      }

      // Fetch items from S3
      const result = await this.listObjects(prefix, "/", maxItems);

      // Extract unique values from item names using common patterns
      const partners = new Set<string>();
      const years = new Set<string>();
      const months = new Set<string>();

      // Pattern: partner_report_YYYY_MM.csv or partner/year/month/file.csv
      const reportPattern = /([^_/]+)_?report[_/](\d{4})[_/](\d{2})/i;
      const folderPattern = /^([^/]+)\/(\d{4})\/(\d{2})\//;

      result.items.forEach((item) => {
        // Try filename pattern first
        const fileMatch = item.name.match(reportPattern);
        if (fileMatch) {
          partners.add(fileMatch[1]);
          years.add(fileMatch[2]);
          months.add(fileMatch[3]);
          return;
        }

        // Try folder hierarchy pattern
        const folderMatch = item.key.match(folderPattern);
        if (folderMatch) {
          partners.add(folderMatch[1]);
          years.add(folderMatch[2]);
          months.add(folderMatch[3]);
        }
      });

      // Cache the extracted metadata
      this.cache.cacheFilterMetadata(partners, years, months);

      errorLogger.info("Successfully extracted filter metadata from S3", {
        operation: "extractAndCacheFilterMetadata",
        partnersCount: partners.size,
        yearsCount: years.size,
        monthsCount: months.size,
      });

      return {
        partners: Array.from(partners).sort(),
        years: Array.from(years).sort(),
        months: Array.from(months).sort(),
      };
    } catch (error) {
      const s3Error = new S3ServiceError(
        "Failed to extract filter metadata from S3",
        "EXTRACT_METADATA_FAILED",
        undefined,
        error instanceof Error ? error : undefined
      );
      errorLogger.error(s3Error, "extractAndCacheFilterMetadata", { prefix, maxItems });
      throw s3Error;
    }
  }

  /**
   * Optimized filtered query using prefixes to minimize S3 API calls
   * Constructs efficient prefix patterns to reduce full-bucket scans
   * 
   * @param filters - Filter criteria (partner, year, month)
   * @param delimiter - Delimiter for hierarchical navigation
   * @param maxKeys - Maximum items per request
   */
  async listObjectsWithFilters(
    filters: {
      partner?: string;
      year?: string;
      month?: string;
    } = {},
    delimiter: string = "/",
    maxKeys: number = 1000
  ): Promise<S3ListResult> {
    try {
      // Build optimized prefix from filters to minimize API calls
      let prefix = "";
      if (filters.partner) {
        prefix = `${filters.partner}/`;
        if (filters.year) {
          prefix += `${filters.year}/`;
          if (filters.month) {
            prefix += `${filters.month}/`;
          }
        }
      }

      // Use regular listing with optimized prefix
      return await this.listObjects(prefix, delimiter, maxKeys, undefined, true);
    } catch (error) {
      const s3Error = new S3ServiceError(
        "Failed to list filtered objects",
        "LIST_FILTERED_OBJECTS_FAILED",
        undefined,
        error instanceof Error ? error : undefined
      );
      errorLogger.error(s3Error, "listObjectsWithFilters", { filters });
      throw s3Error;
    }
  }

  /**
   * Get aggregate statistics for a prefix (cached results)
   * Useful for dashboard summaries without full item enumeration
   * 
   * @param prefix - Prefix to get stats for
   */
  async getPrefixStatistics(prefix: string = ""): Promise<{
    totalItems: number;
    totalSize: number;
    fileCount: number;
    folderCount: number;
    lastModified?: Date;
  }> {
    try {
      const result = await this.listObjects(prefix, "/", 1000, undefined, true);

      let totalSize = 0;
      let fileCount = 0;
      let folderCount = 0;
      let lastModified: Date | undefined;

      result.items.forEach((item) => {
        if (item.type === "file") {
          fileCount++;
          totalSize += (item as S3Object).size || 0;
          const itemDate = (item as S3Object).lastModified;
          if (!lastModified || itemDate > lastModified) {
            lastModified = itemDate;
          }
        } else {
          folderCount++;
        }
      });

      return {
        totalItems: result.totalCount,
        totalSize,
        fileCount,
        folderCount,
        lastModified,
      };
    } catch (error) {
      const s3Error = new S3ServiceError(
        "Failed to get prefix statistics",
        "PREFIX_STATS_FAILED",
        undefined,
        error instanceof Error ? error : undefined
      );
      errorLogger.error(s3Error, "getPrefixStatistics", { prefix });
      throw s3Error;
    }
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): {
    size: number;
    maxSize: number;
    ttlMs: number;
    hitRate?: number;
  } {
    return this.cache.getStats();
  }

  /**
   * Get detailed cache performance report
   */
  getCachePerformanceReport(): string {
    return this.cache.getPerformanceReport();
  }

  /**
   * Log cache performance report to console
   */
  logCachePerformance(): void {
    this.cache.logPerformanceReport();
  }

  /**
   * Reset cache statistics (useful for benchmarking)
   */
  resetCacheStatistics(): void {
    this.cache.resetStatistics();
  }

  /**
   * Manually trigger cache cleanup
   */
  cleanupCache(): void {
    this.cache.cleanup();
  }

  /**
   * Warm cache by pre-loading frequently accessed prefixes
   * @param prefixes - Array of prefixes to pre-load
   * @param options - Warming options
   */
  async warmCache(
    prefixes: string[],
    options: {
      delimiter?: string;
      maxKeys?: number;
      parallel?: boolean;
    } = {}
  ): Promise<{ success: number; failed: number; totalMs: number }> {
    const { delimiter = "/", maxKeys = 1000, parallel = true } = options;
    const startTime = Date.now();
    let success = 0;
    let failed = 0;

    const warmPrefix = async (prefix: string) => {
      try {
        await this.listObjects(prefix, delimiter, maxKeys, undefined, true);
        this.cache.markPrefixAsWarmed(prefix);
        success++;
        errorLogger.info(`Cache warmed for prefix: "${prefix}"`, {
          operation: "warmCache",
          prefix,
        });
      } catch (error) {
        failed++;
        errorLogger.warn(`Failed to warm cache for prefix: "${prefix}"`, {
          operation: "warmCache",
          prefix,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };

    if (parallel) {
      await Promise.all(prefixes.map(warmPrefix));
    } else {
      for (const prefix of prefixes) {
        await warmPrefix(prefix);
      }
    }

    const totalMs = Date.now() - startTime;

    errorLogger.info(`Cache warming completed`, {
      operation: "warmCache",
      success,
      failed,
      totalMs,
      prefixCount: prefixes.length,
    });

    return { success, failed, totalMs };
  }

  /**
   * Get cache health status
   */
  getCacheHealthStatus(): string {
    return this.cache.getHealthStatus();
  }

  /**
   * Perform cache health check
   */
  performCacheHealthCheck(): {
    healthy: boolean;
    issues: string[];
    recommendations: string[];
  } {
    return this.cache.performHealthCheck();
  }

  /**
   * Get top accessed prefixes for identifying cache warming candidates
   */
  getTopAccessedPrefixes(n: number = 10): Array<{ prefix: string; count: number }> {
    return this.cache.getTopPrefixes(n);
  }
}

// Export a default instance
let defaultS3Service: S3Service | null = null;

export function getS3Service(): S3Service {
  if (!defaultS3Service) {
    defaultS3Service = new S3Service();
    console.log('[S3Service] Created new S3Service instance');
  }
  
  // WORKAROUND: Check and fix upload methods on EVERY call
  // This fixes a webpack/HMR caching issue where these methods don't appear at runtime
  if (typeof defaultS3Service.uploadFile !== 'function') {
    console.log('[S3Service] uploadFile missing, attempting to fix...');
    const proto = Object.getPrototypeOf(defaultS3Service);
    
    if (typeof proto.uploadFile === 'function') {
      console.log('[S3Service] Found uploadFile on prototype, binding to instance');
      defaultS3Service.uploadFile = proto.uploadFile.bind(defaultS3Service);
    } else {
      console.error('[S3Service] uploadFile NOT FOUND on prototype either!', {
        protoMethods: Object.getOwnPropertyNames(proto),
      });
    }
  }
  
  if (typeof defaultS3Service.uploadFileMultipart !== 'function') {
    const proto = Object.getPrototypeOf(defaultS3Service);
    if (typeof proto.uploadFileMultipart === 'function') {
      defaultS3Service.uploadFileMultipart = proto.uploadFileMultipart.bind(defaultS3Service);
    }
  }
  
  if (typeof defaultS3Service.uploadFiles !== 'function') {
    const proto = Object.getPrototypeOf(defaultS3Service);
    if (typeof proto.uploadFiles === 'function') {
      defaultS3Service.uploadFiles = proto.uploadFiles.bind(defaultS3Service);
    }
  }
  
  if (typeof defaultS3Service.abortUpload !== 'function') {
    const proto = Object.getPrototypeOf(defaultS3Service);
    if (typeof proto.abortUpload === 'function') {
      defaultS3Service.abortUpload = proto.abortUpload.bind(defaultS3Service);
    }
  }
  
  console.log('[S3Service] Returning service instance', {
    hasUploadFile: typeof defaultS3Service.uploadFile,
    hasUploadFileMultipart: typeof defaultS3Service.uploadFileMultipart,
    hasUploadFiles: typeof defaultS3Service.uploadFiles,
    hasAbortUpload: typeof defaultS3Service.abortUpload,
  });
  
  return defaultS3Service;
}

// Reset function for development/testing
export function resetS3Service(): void {
  defaultS3Service = null;
}

export default S3Service;
